import {Rectangle, Point} from 'pixi.js';
import {StickyTo} from '@gongxigames/framework/core/fragments/react';
import {ScreenOrientation} from '@gongxigames/framework/core/types';

export type FullScreenScaleProps = {
    scale: number;
    x: number;
    y: number;
};

export class ScreenParameters {
    public readonly isPortraitOrientation: boolean;
    public readonly isLandscapeOrientation: boolean;

    constructor(
        public readonly orientation: ScreenOrientation,
        public readonly canvasWidth: number,
        public readonly canvasHeight: number,
        public readonly width: number,
        public readonly height: number,
        public readonly scale: number,
        public readonly x: number,
        public readonly y: number
    ) {
        this.isPortraitOrientation = orientation === ScreenOrientation.PORTRAIT;
        this.isLandscapeOrientation = orientation === ScreenOrientation.LANDSCAPE;
    }

    public isEqual(other: ScreenParameters): boolean {
        return this.x === other.x &&
            this.y === other.y &&
            this.width === other.width &&
            this.height === other.height &&
            this.canvasWidth === other.canvasWidth &&
            this.canvasHeight === other.canvasHeight &&
            this.scale === other.scale &&
            this.orientation === other.orientation;
    }

    public getScreenBounds(): Rectangle {
        const x = Math.ceil(Math.ceil(this.x) / this.scale);
        const y = Math.ceil(Math.ceil(this.y) / this.scale);
        const width = this.width + x * 2;
        const height = this.height + y * 2;

        return new Rectangle(-x, -y, width, height);
    }

    public getStickyPosition(position: Point, stickyTo: StickyTo, fullscreen?: boolean): Point {
        let width = this.width;
        let height = this.height;
        const stickyPosition = new Point(this.width / 2, this.height / 2);

        if (fullscreen) {
            width += this.x * 2 / this.scale;
            height += this.y * 2 / this.scale;
            stickyPosition.set(width / 2 - (this.x / this.scale), height / 2 - (this.y / this.scale));
        }

        stickyPosition.x += position.x;
        stickyPosition.y += position.y;

        if (stickyTo.includes('top')) {
            stickyPosition.y -= height / 2;
        }

        if (stickyTo.includes('bottom')) {
            stickyPosition.y += height / 2;
        }

        if (stickyTo.includes('left')) {
            stickyPosition.x -= width / 2;
        }

        if (stickyTo.includes('right')) {
            stickyPosition.x += width / 2;
        }

        return stickyPosition;
    }

    public calcScreenFillProps(currentWidth: number, currentHeight: number): FullScreenScaleProps {
        const {scale, x, y, width, height} = this;

        const screenWidth = x / scale * 2 + width;
        const screenHeight = y / scale * 2 + height;

        const scaleX = screenWidth / currentWidth;
        const scaleY = screenHeight / currentHeight;

        const newScale = Math.max(scaleX, scaleY);

        const containerWidth = currentWidth * newScale;
        const containerHeight = currentHeight * newScale;

        const offsetX = (screenWidth - containerWidth) / 2;
        const offsetY = (screenHeight - containerHeight) / 2;

        const newX = -x / scale + offsetX;
        const newY = -y / scale + offsetY;

        return {scale: newScale, x: newX, y: newY};
    }
}

